/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ActionButton, ActionButtonType, Callout, Checkbox, List } from "@octopusdeploy/design-system-components";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { Repository, PlatformHubGitCredentialResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { GitCredentialPopover } from "~/areas/library/components/GitCredentials/SharedGitCredentialForm";
import FeatureToggleVisibility from "~/components/FeatureToggle/New/FeatureToggleVisibility";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip/index";
import InputWithActions from "~/components/InputWithActions/index";
import Markdown from "~/components/Markdown";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import { Text, ExpandableFormSection, Summary, required, MarkdownEditor, Sensitive, Note } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
interface PlatformHubGitCredentialPageProps {
    loaderData: PlatformHubGitCredentialLoaderData;
    isCreate: boolean;
}
interface PlatformHubGitCredentialLoaderData {
    gitCredential: PlatformHubGitCredentialResource;
}
export async function platformHubGitCredentialPageLoader(repository: Repository, gitCredentialId: string): Promise<PlatformHubGitCredentialLoaderData> {
    const gitCredential = await repository.PlatformHubGitCredentials.getById(gitCredentialId);
    return { gitCredential };
}
export function PlatformHubGitCredentialPage({ loaderData, isCreate }: PlatformHubGitCredentialPageProps) {
    const { navigate } = useSpaceAwareNavigation();
    const [newAllowListEntry, setNewAllowListEntry] = React.useState<string>("");
    const { model, setModel, formProps, createSaveAction, getFieldError, isSubmitting } = useForm({
        initialModel: loaderData.gitCredential,
        formName: "Save Platform Hub Git Credential",
        onSubmit: async (repository: Repository, model) => handleSaveAction(repository, model),
        afterSubmit: async (repository, updatedModel) => {
            if (isCreate)
                return navigate(links.platformHubGitCredentialPage.generateUrl({ gitCredentialId: updatedModel.Id }));
        },
        submitPermission: { permission: Permission.PlatformHubEdit },
    });
    const title = isCreate ? "Create Git Credential" : model.Name;
    const descriptionSummary = () => (model.Description ? Summary.summary(<Markdown markup={model.Description}/>) : Summary.placeholder("No credential description provided"));
    const credentialSummary = () => Summary.summary("Username and password");
    const allowedRepositoriesSummary = () => Summary.summary(model.RepositoryRestrictions.Enabled ? "Only allow specified repositories" : "Allow all repositories");
    const allowedRepositoriesContainsHttpScheme = () => model.RepositoryRestrictions.AllowedRepositories.some((u) => u.toLowerCase().startsWith("http://"));
    const updateAllowedRepository = (name: string, index: number) => {
        const modifiedList = [...model.RepositoryRestrictions.AllowedRepositories];
        modifiedList[index] = name;
        setModel({ ...model, RepositoryRestrictions: { ...model.RepositoryRestrictions, AllowedRepositories: modifiedList } });
    };
    const removeAllowedRepository = (index: number) => {
        const modifiedList = [...model.RepositoryRestrictions.AllowedRepositories];
        modifiedList.splice(index, 1);
        setModel({ ...model, RepositoryRestrictions: { ...model.RepositoryRestrictions, AllowedRepositories: modifiedList } });
    };
    const handleSaveAction = async (repository: Repository, gitCredentialResource: PlatformHubGitCredentialResource) => {
        if (isCreate) {
            const createResponse = await repository.PlatformHubGitCredentials.create(gitCredentialResource);
            return await repository.PlatformHubGitCredentials.getById(createResponse.Id);
        }
        await repository.PlatformHubGitCredentials.modify(gitCredentialResource);
        return gitCredentialResource;
    };
    const { execute: deleteGitCredential } = useMutation({
        name: "deleteGitCredential",
        action: async (repository: Repository, gitCredentialResource: PlatformHubGitCredentialResource) => await repository.PlatformHubGitCredentials.delete(gitCredentialResource.Id),
        afterComplete: async () => navigate(links.platformHubGitCredentialsPage.generateUrl()),
    });
    const overflowActions = isCreate
        ? OverflowMenuConverterVNext.convertAll([])
        : OverflowMenuConverterVNext.convertAll([OverflowMenuItems.deleteItemDefault("Git credential", () => deleteGitCredential(model), { permission: Permission.PlatformHubEdit })]);
    return (<PageContent header={{
            primaryAction: createSaveAction({}),
            title,
            breadcrumbs: [{ label: "Git Credentials", pageUrl: links.platformHubGitCredentialsPage.generateUrl() }],
            overflowActions: overflowActions.menuItems,
        }}>
            {overflowActions.dialogs}
            <Form {...formProps} expandAllOnMount={isCreate}>
                <ExpandableFormSection errorKey="Name" title="Name" focusOnExpandAll summary={model.Name ? Summary.summary(model.Name) : Summary.placeholder("Enter a name for your Git credential")} help="Enter a name for your Git credential">
                    <Text value={model.Name} onChange={(Name) => setModel({ ...model, Name })} label="Git credential name" validate={required("Enter a Git credential name")} error={getFieldError("Name")} autoFocus/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="description" title="Description" summary={descriptionSummary()} help="Enter a description for your Git credential">
                    <MarkdownEditor value={model.Description} label="Git credential description" onChange={(Description) => setModel({ ...model, Description })}/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="details" title="Credentials" summary={credentialSummary()} help="Enter your Git credential details">
                    <div>
                        We recommend using a personal access token with limited privilege <GitCredentialPopover />
                    </div>
                    <Text key="Username" value={model.Details.Username} onChange={(Username) => setModel({ ...model, Details: { ...model.Details, Username } })} label="Username" error={getFieldError("Username")} validate={required("Enter authentication details.")} disabled={isSubmitting}/>
                    <Sensitive key="Password" value={model.Details.Password} onChange={(Password) => setModel({ ...model, Details: { ...model.Details, Password } })} label="Personal Access Token or Password" error={getFieldError("Password")} disabled={isSubmitting}/>
                    <Note>
                        GitLab and GitHub <b>require</b> token-based authentication (excludes Github Enterprise Server).
                    </Note>
                </ExpandableFormSection>
                <FeatureToggleVisibility toggle="GitCredentialAllowListFeatureToggle">
                    <ExpandableFormSection errorKey="allowedRepositories" title="Allowed Repositories" summary={allowedRepositoriesSummary()} help="Control which repositories are allowed to use this Git credential.">
                        <Checkbox value={model.RepositoryRestrictions.Enabled} onChange={(Enabled) => setModel({ ...model, RepositoryRestrictions: { ...model.RepositoryRestrictions, Enabled } })} label="Only allow specified Git repositories"/>

                        {model.RepositoryRestrictions.Enabled && (<>
                                {allowedRepositoriesContainsHttpScheme() && (<Callout hideTitle={true} type={"warning"}>
                                        Use HTTPS to ensure secure connections. HTTP is supported, but not recommended.
                                    </Callout>)}

                                <InputWithActions input={<Text value={newAllowListEntry} label="HTTPS Git repository URL" onChange={(newAllowListEntry) => setNewAllowListEntry(newAllowListEntry)}/>} actions={<ActionButton label={"Add to list"} type={ActionButtonType.Secondary} onClick={() => {
                    setModel({ ...model, RepositoryRestrictions: { ...model.RepositoryRestrictions, AllowedRepositories: [...model.RepositoryRestrictions.AllowedRepositories, newAllowListEntry] } });
                    setNewAllowListEntry("");
                }}/>}/>
                                <Note>
                                    Use <code>*</code> at the end of URLs to include all repositories in that path.
                                    <br />
                                    Example:{" "}
                                    <code>
                                        https://
                                        <wbr />
                                        github.com/
                                        <wbr />
                                        OctopusSamples/
                                        <wbr />*
                                    </code>
                                </Note>
                                <List items={model.RepositoryRestrictions.AllowedRepositories} renderRow={({ item: url, index }) => (<InputWithActions input={<Text value={url} label="" onChange={(name) => updateAllowedRepository(name, index)}/>} actions={<IconButtonWithTooltip toolTipContent="Remove repository" icon={"XmarkIcon"} accessibleName={"Remove allowed repository"} onClick={() => removeAllowedRepository(index)}/>}/>)}/>
                            </>)}
                    </ExpandableFormSection>
                </FeatureToggleVisibility>
            </Form>
        </PageContent>);
}
